import {
  EPropertyType,
  ESearchLevel,
  ICluster,
  IPoint,
  IStacked
} from '../../../../types-library/src'
import { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'

import {
  convertParamsToNewAPI,
  getPolygon,
  getPropertiesFromVectorTile
} from '../../../../apis-library/src'
import {
  useFilterStore,
  useMapStore,
  usePointStore
} from '../../../../state-library/src'
import {
  getDesiredZoomLevel,
  getMapBounds,
  getQuickLinkState
} from '../../utils'

export const MIN_DEFAULT_FOR_RENT = 1500
export const MIN_DEFAULT_FOR_SALE = 1000000
export const MAX_DEFAULT_FOR_RENT = 130000
export const MAX_DEFAULT_FOR_SALE = 40000000

let requiredTiles: {
  spatialIndex: string
}[] = []

interface Props {
  children?: React.ReactNode
}

/**
 * This component is used to render the tiler in the map
 * @returns {JSX.Element}
 */
const PointsProvider = ({ children }: Props): JSX.Element => {
  const { map, isFlyTo } = useMapStore()

  let timeout: NodeJS.Timeout | undefined

  const {
    tileQueryParams,
    pointsSvgContainer,
    isEnablePropertiesPoints,

    setTileQueryParams,
    setPointsSvgContainer,

    addLoadedPoints,
    addLoadedClusters,
    addLoadedStackeds,
    clearLoadedPoints,
    clearLoadedClusters,
    clearLoadedStackeds
  } = usePointStore()

  const {
    latitude,
    longitude,
    searchLevel,
    propertyType,
    constructionType,
    presale,
    priceFrom,
    priceTo,
    rooms,
    opportunityCriteria,
    bathrooms,
    parkings,
    amenities,
    publishedDate,
    priceDiffFrom,
    priceDiffTo,
    capRateFrom,
    capRateTo,
    priceBySqmFrom,
    areaBounds,
    priceBySqmTo,
    searchAddress,
    pamRequestId,
    constructionYearFrom,
    constructionYearTo,
    showOnlyCommissionProperties,
    associationIds,
    setAreaBounds,
    origin,
    commissionRateLowRange,
    commissionRateHighRange,
    surfaceFrom,
    surfaceTo,
    listingsSource,
    wiggotUserType,
    locations
  } = useFilterStore((state) => state)
  const tileQueryParamsRef = useRef<typeof tileQueryParams>(tileQueryParams)
  tileQueryParamsRef.current = tileQueryParams

  const isEnablePropertiesPointsRef = useRef<boolean>(isEnablePropertiesPoints)
  isEnablePropertiesPointsRef.current = isEnablePropertiesPoints
  const isFlyToRef = useRef<boolean>(isFlyTo)
  isFlyToRef.current = isFlyTo

  const [isLoadedQueryParams, setIsLoadedQueryParams] = useState(false)

  /**
   *  This function is used to load the tiles in the map by tiles
   * @returns {Promise<void>}
   */
  const runClustersByTiles = async (): Promise<void> => {
    if (!tileQueryParamsRef?.current || !map) {
      return
    }

    const { minX, minY, maxX, maxY, zIndex } = getQuadrantsBounds()

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const spatialIndex = `${zIndex}-${x}-${y}`

        if (requiredTiles.some((tile) => tile.spatialIndex === spatialIndex)) {
          continue
        }

        requiredTiles.push({
          spatialIndex
        })

        getListingsOfTile({
          xIndex: x,
          yIndex: y,
          zIndex
        })
          .then(({ clusters, points, stackeds, urlKey }) => {
            addLoadedPoints({
              key: urlKey,
              spatialIndices: {
                xIndex: x,
                yIndex: y,
                zIndex
              },
              features: points
            })

            addLoadedClusters({
              key: urlKey,
              spatialIndices: {
                xIndex: x,
                yIndex: y,
                zIndex
              },
              features: clusters
            })

            addLoadedStackeds({
              key: urlKey,
              spatialIndices: {
                xIndex: x,
                yIndex: y,
                zIndex
              },
              features: stackeds
            })
          })
          .catch((error) => {
            console.log(error)
          })
      }
    }
  }

  const getMinPriceM2 = (min: number, max: number) => {
    if (max && !min) {
      return 1
    }

    if (min) {
      return min
    }
    return undefined
  }

  const getMinDiscountValue = (priceDiff: { min: number; max: number }) => {
    if (priceDiff.max && priceDiff.min === 0) {
      return 0
    }

    if (priceDiff.min === 0) {
      return undefined
    }
    return priceDiff.min
  }

  const returnPriceToValidated = (
    priceTo: number,
    priceFrom: number,
    propertyType: EPropertyType | undefined
  ) => {
    if (isNaN(priceTo) || priceTo === null) {
      const maxDefault =
        propertyType === EPropertyType.SALE
          ? MAX_DEFAULT_FOR_SALE
          : MAX_DEFAULT_FOR_RENT
      const price = priceFrom > maxDefault ? priceFrom : priceTo
      return price
    } else {
      return priceTo
    }
  }

  /**
   * This function is used to create the query params for the tiler
   * @returns {Promise<string>}
   */
  const createQueryParams = async (): Promise<void> => {
    setIsLoadedQueryParams(false)

    if (!pamRequestId) {
      const id = (await getPolygonId()) || {}

      if (!id) {
        if (!map) {
          return
        }

        const bounds = getMapBounds({ map })

        if (!bounds?.length) {
          return
        }

        setAreaBounds(bounds)
        return
      }
    }

    // Al dibujar una zona en el mapa (areaBounds con datos), /properties/wiggot
    // siempre debe recibir searchLevel=state, sin importar el nivel de zoom
    // que haya calculado el drawing tool (ese searchLevel de zoom es el que
    // usan filter-summary/insights, y no debe tocarse aqui).
    let level: ESearchLevel
    if (areaBounds.length > 0) {
      level = ESearchLevel.STATE
    } else {
      level = searchLevel
      if ([ESearchLevel.PROPERTY, ESearchLevel.BlOCK].includes(searchLevel)) {
        level = ESearchLevel.NEIGHBORHOOD
      }
    }

    const quickLinkState = getQuickLinkState(searchAddress, {
      latitude: latitude,
      longitude: longitude
    })

    const assignCapRate = (value: number) => {
      return capRateTo === 0 && capRateFrom === 0 ? undefined : value
    }

    // arrayCoordinates siempre debe ser el mismo formato de string plano
    // "[lng,lat],[lng,lat],..." que boundsFilter, nunca el JSON de objetos
    // {latitude,longitude}. Prioriza el area dibujada (areaBounds) sobre la
    // seleccion de varios vecindarios (locations), ya que son mutuamente
    // excluyentes en la UI.
    const arrayCoordinates =
      areaBounds.length > 0
        ? areaBounds.map((bound) => `[${bound.join(',')}]`).join(',')
        : locations.length > 0
          ? locations
              .map(({ latitude, longitude }) => `[${longitude},${latitude}]`)
              .join(',')
          : undefined

    const queryParams: { [key: string]: any } = {
      bathrooms,
      bedrooms: rooms,
      propertyTypes: constructionType || undefined,
      presale: presale || undefined,
      parkings,
      searchLevel: level,
      amenities: amenities?.length ? amenities?.join(',') : undefined,
      type:
        propertyType === EPropertyType.SALE
          ? EPropertyType.SALE
          : EPropertyType.RENT,
      latitude,
      longitude,
      proxyPublishedDays: publishedDate,
      // boundsFilter ya no se manda a /properties/wiggot: esta API nueva no
      // lo usa (convertParamsToNewAPI lo descarta) y arrayCoordinates ahora
      // cubre exactamente el mismo caso (dibujo) con el formato correcto.
      budgetM2HighRange: priceBySqmTo || undefined,
      budgetM2LowRange: getMinPriceM2(priceBySqmFrom ?? 0, priceBySqmTo ?? 0),
      caprateHighRange: assignCapRate(capRateTo),
      caprateLowRange: assignCapRate(capRateFrom),
      discountHighRange: priceDiffTo || undefined,
      discountLowRange: getMinDiscountValue({
        min: priceDiffFrom ?? 0,
        max: priceDiffTo ?? 0
      }),
      quickLinkState,
      pamRequestId,
      builtYearFrom: constructionYearFrom || undefined,
      builtYearTo: constructionYearTo || undefined,
      origin,
      showOnlyCommissionProperties,
      associationIds,
      commissionRateLowRange: commissionRateHighRange || undefined,
      commissionRateHighRange: commissionRateLowRange || undefined,
      surfaceFrom: surfaceTo && !surfaceFrom ? 0 : surfaceFrom,
      surfaceTo: surfaceFrom && !surfaceTo ? 1000000 : surfaceTo,
      budgetLowRange: priceFrom,
      budgetHighRange: returnPriceToValidated(priceTo, priceFrom, propertyType),
      opportunityCriteria,
      listingsSource,
      wiggotUserType,
      arrayCoordinates
    }

    Object.keys(queryParams)?.forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key]
      }
    })
    const urlParams = new URLSearchParams(
      convertParamsToNewAPI(queryParams)
    ).toString()
    setTileQueryParams(urlParams)
    setIsLoadedQueryParams(true)
  }

  /**
   * This function is used to get the tile bounds from the map
   * @returns {minX: number, minY: number, maxX: number, maxY: number, zIndex: number}
   */
  function getQuadrantsBounds(): {
    minX: number
    minY: number
    maxX: number
    maxY: number
    zIndex: number
  } {
    const bounds = map?.getBounds()

    const mapZoom = map?.getZoom() || 0

    const { zoom } = getDesiredZoomLevel({ zoom: mapZoom })

    const southWest = getSpatialIndices({
      lat: bounds?.getSouthWest()?.lat || 0,
      lng: bounds?.getSouthWest()?.lng || 0,
      zoom
    })

    const northEast = getSpatialIndices({
      lat: bounds?.getNorthEast()?.lat || 0,
      lng: bounds?.getNorthEast()?.lng || 0,
      zoom
    })

    return {
      minX: Math.min(southWest?.xIndex, northEast?.xIndex),
      minY: Math.min(southWest?.yIndex, northEast?.yIndex),
      maxX: Math.max(southWest?.xIndex, northEast?.xIndex),
      maxY: Math.max(southWest?.yIndex, northEast?.yIndex),
      zIndex: zoom
    }
  }

  /**
   * This function is used to get the listings of the tile
   * @param xIndex The x value
   * @param yIndex The y value
   * @param zIndex The z value
   * @returns {Promise<{listings: ListingPoint[], clusters: ListingCluster[]}>}
   */
  const getListingsOfTile = async (params: {
    xIndex: number
    yIndex: number
    zIndex: number
  }): Promise<{
    points: IPoint[]
    clusters: ICluster[]
    stackeds: IStacked[]
    urlKey: string
  }> => {
    const { xIndex, yIndex, zIndex } = params

    const { isSuccess, data } = await getPropertiesFromVectorTile({
      queryParams: `${zIndex}/${xIndex}/${yIndex}?${tileQueryParamsRef?.current}`
    })

    if (!isSuccess || !data) {
      return {
        points: [],
        clusters: [],
        stackeds: [],
        urlKey: tileQueryParamsRef?.current || ''
      }
    }
    return {
      ...data,
      urlKey: tileQueryParamsRef?.current || ''
    }
  }

  /**
   * This function is used to get the spatial indices
   * @param lat The latitude
   * @param lng The longitude
   * @param zoom The zoom
   * @returns {xIndex: number, yIndex: number, zIndex: number}
   */
  function getSpatialIndices(params: {
    lat: number
    lng: number
    zoom: number
  }): {
    xIndex: number
    yIndex: number
    zIndex: number
  } {
    const { lat, lng, zoom } = params

    // eslint-disable-next-line prefer-exponentiation-operator
    const xIndex = Math.floor(((lng + 180) / 360) * Math.pow(2, zoom))
    const yIndex = Math.floor(
      ((1 -
        Math.log(
          Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
        ) /
          Math.PI) /
        2) *
        // eslint-disable-next-line prefer-exponentiation-operator
        Math.pow(2, zoom)
    )
    return { xIndex, yIndex, zIndex: zoom }
  }

  /**
   * This function is used to get the polygon id
   * @returns {Promise<string | undefined>}
   */
  const getPolygonId = async (): Promise<string | undefined> => {
    let level: ESearchLevel

    switch (searchLevel) {
      case ESearchLevel.PROPERTY:
      case ESearchLevel.BlOCK:
        level = ESearchLevel.NEIGHBORHOOD
        break
      default:
        level = searchLevel
        break
    }

    const { isSuccess, data } = await getPolygon({
      center: {
        lat: latitude,
        lng: longitude
      },
      searchLevel: level
    })

    if (!isSuccess || !data) {
      return undefined
    }

    return data?.id
  }

  const createSVGContainer = (): void => {
    if (!map) {
      return
    }

    const findSvg = document.getElementById('listings-svg-container')
    if (findSvg) {
      return
    }

    const container = map?.getCanvasContainer()
    const svg = d3
      .select(container)
      .append('svg')
      .attr('id', 'listings-svg-container')
      .attr('width', '100%')
      .attr('height', '100%')
      .style('position', 'absolute')

    setPointsSvgContainer(svg)
  }

  useEffect(() => {
    const onMoveEnd = () => {
      if (!isEnablePropertiesPointsRef?.current) {
        return
      }
      clearTimeout(timeout)
      timeout = setTimeout(runClustersByTiles, 500)
    }

    // if (map && queryParams && !isLoadingHistogram && isEnablePropertiesPoints) {
    if (map && tileQueryParams && isEnablePropertiesPoints) {
      map.on('moveend', onMoveEnd)
    }

    return () => {
      map?.off('moveend', onMoveEnd)
    }
  }, [
    map,
    tileQueryParams,
    // isLoadingHistogram,
    opportunityCriteria,
    isEnablePropertiesPoints
  ])

  useEffect(() => {
    if (!map) {
      return
    }

    pointsSvgContainer?.selectAll('*')?.remove()
    clearLoadedPoints()
    clearLoadedClusters()
    clearLoadedStackeds()

    requiredTiles = []
    setTileQueryParams('')

    createQueryParams()
  }, [
    map,
    priceFrom,
    priceTo,
    latitude,
    longitude,
    propertyType,
    searchLevel,
    constructionType,
    presale,
    opportunityCriteria,
    rooms,
    bathrooms,
    parkings,
    amenities,
    publishedDate,
    areaBounds,
    priceDiffFrom,
    priceDiffTo,
    capRateFrom,
    capRateTo,
    priceBySqmFrom,
    priceBySqmTo,
    // isLoadingHistogram,
    pamRequestId,
    constructionYearFrom,
    constructionYearTo,
    showOnlyCommissionProperties,
    associationIds,
    origin,
    commissionRateLowRange,
    commissionRateHighRange,
    surfaceFrom,
    surfaceTo,
    listingsSource,
    wiggotUserType
  ])

  useEffect(() => {
    if (!isEnablePropertiesPoints) {
      pointsSvgContainer?.selectAll('*')?.remove()
      setPointsSvgContainer(undefined)
      document.getElementById('listings-svg-container')?.remove()

      requiredTiles = []

      clearLoadedPoints()
      clearLoadedClusters()
      clearLoadedStackeds()

      // setListingCardHovered(undefined)
    }
  }, [isEnablePropertiesPoints, pointsSvgContainer])

  useEffect(() => {
    if (tileQueryParams && isLoadedQueryParams && isEnablePropertiesPoints) {
      createSVGContainer()
      runClustersByTiles()
    }
  }, [tileQueryParams, isLoadedQueryParams, isEnablePropertiesPoints])

  return <>{children}</>
}

export default PointsProvider
